import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Linkedin, Search } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const EMPTY = { name: "", linkedin: "", function_id: "", notes: "" };
const inputCls = "mt-1 w-full h-10 px-3 border border-slate-300 rounded-sm text-sm outline-none focus:border-slate-900";
const href = (u) => (u.startsWith("http") ? u : `https://${u}`);

const TalentDialog = ({ row, functions, onClose, onSave, saving }) => {
  const [f, setF] = useState({ ...EMPTY, ...row });
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="talent-dialog" className="sm:max-w-lg">
        <DialogHeader><DialogTitle>{row?.id ? "Edit candidate" : "Add candidate"}</DialogTitle></DialogHeader>
        <label className="block text-xs font-semibold text-slate-600">Name *
          <input data-testid="talent-name" autoFocus value={f.name} onChange={set("name")} className={inputCls} /></label>
        <label className="block text-xs font-semibold text-slate-600">LinkedIn link
          <input data-testid="talent-linkedin" value={f.linkedin} onChange={set("linkedin")} placeholder="https://www.linkedin.com/in/…" className={inputCls} /></label>
        <label className="block text-xs font-semibold text-slate-600">Function
          <select data-testid="talent-function" value={f.function_id} onChange={set("function_id")} className={`${inputCls} bg-white`}>
            <option value="">— Select —</option>
            {functions.map((fn) => <option key={fn.id} value={fn.id}>{fn.name}</option>)}
          </select>
          {!functions.length && <span className="text-[11px] font-normal text-amber-700">Add functions in Masters → HR · Talent Pipeline Master</span>}
        </label>
        <label className="block text-xs font-semibold text-slate-600">Notes
          <textarea data-testid="talent-notes" rows={4} value={f.notes} onChange={set("notes")} className={`${inputCls} h-auto py-2`} /></label>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="talent-save" disabled={!f.name.trim() || saving} onClick={() => onSave(f)} className="bg-slate-900 hover:bg-slate-800">{saving ? "Saving…" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const TalentPipeline = () => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [fn, setFn] = useState("");
  const { data: rows = [] } = useQuery({ queryKey: ["talent"], queryFn: async () => (await api.get("/hr/talent")).data });
  const { data: functions = [] } = useQuery({ queryKey: ["talent-functions"], queryFn: async () => (await api.get("/hr/talent/functions")).data });
  const done = (msg) => { qc.invalidateQueries({ queryKey: ["talent"] }); toast.success(msg); setEditing(null); };
  const err = (e) => toast.error(e?.response?.data?.detail || "Couldn't save");
  const save = useMutation({
    mutationFn: (f) => (f.id ? api.put(`/hr/talent/${f.id}`, f) : api.post("/hr/talent", f)),
    onSuccess: (_, f) => done(f.id ? "Candidate updated" : "Candidate added"), onError: err,
  });
  const del = useMutation({ mutationFn: (id) => api.delete(`/hr/talent/${id}`), onSuccess: () => done("Candidate removed"), onError: err });
  const shown = useMemo(() => rows.filter((r) => (!fn || r.function_id === fn) &&
    (!q.trim() || `${r.name} ${r.notes}`.toLowerCase().includes(q.trim().toLowerCase()))), [rows, q, fn]);

  return (
    <section data-testid="talent-pipeline" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="talent-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or notes" className="w-full h-10 pl-9 pr-3 border border-slate-300 rounded-sm text-sm" />
        </div>
        <select data-testid="talent-filter-function" value={fn} onChange={(e) => setFn(e.target.value)} className="h-10 px-2 border border-slate-300 rounded-sm text-sm bg-white">
          <option value="">All functions</option>
          {functions.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <Button data-testid="talent-add" onClick={() => setEditing({})} className="h-10 bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4 mr-1" /> Add candidate</Button>
      </div>
      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] tracking-[0.15em] uppercase text-slate-500">
            <tr><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">LinkedIn</th><th className="text-left px-3 py-2">Function</th><th className="text-left px-3 py-2">Notes</th><th className="text-left px-3 py-2">Updated</th><th /></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {shown.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No candidates yet.</td></tr>}
            {shown.map((r, i) => (
              <tr key={r.id} data-testid={`talent-row-${i}`} className="align-top">
                <td className="px-3 py-2 font-semibold text-slate-900">{r.name}</td>
                <td className="px-3 py-2">{r.linkedin ? <a data-testid={`talent-linkedin-${i}`} href={href(r.linkedin)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-700 hover:underline"><Linkedin className="h-4 w-4" /> Profile</a> : "—"}</td>
                <td className="px-3 py-2">{r.function_name || "—"}</td>
                <td className="px-3 py-2 text-slate-600 whitespace-pre-wrap max-w-md">{r.notes || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{new Date(r.updated_at).toLocaleDateString("en-GB")} · {r.updated_by}</td>
                <td className="px-3 py-2 whitespace-nowrap text-right">
                  <button data-testid={`talent-edit-${i}`} onClick={() => setEditing(r)} className="text-slate-400 hover:text-slate-900 mr-2"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`talent-delete-${i}`} onClick={() => window.confirm(`Remove ${r.name}?`) && del.mutate(r.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <TalentDialog row={editing} functions={functions} onClose={() => setEditing(null)} saving={save.isPending} onSave={(f) => save.mutate(f)} />}
    </section>
  );
};
